/* eslint-disable no-console */
/**
 * Opt-in dev-mode debugger for route stop operations.
 *
 * Off by default. Enable via `REACT_APP_DEBUG_ROUTE_OPS=true` or set
 * `window.__debugRouteOps = true` in the DevTools console. All helpers
 * no-op when the flag is off — safe to leave in production builds. Filter
 * Chrome's console by `[RouteOps]` to isolate these lines.
 */
import { hasRecurrenceOnDate } from './recurrence'

// Stable filter token — external tooling and saved DevTools presets depend on it.
const TAG_PREFIX = '[RouteOps]'
const TAG = `%c${TAG_PREFIX}`
const TAG_STYLE = 'color:#0ea5e9;font-weight:bold;'
const OK = '✓'
const FAIL = '✗'
const INFO = 'ⓘ'

const ENV_FLAG = String(process.env.REACT_APP_DEBUG_ROUTE_OPS || '').toLowerCase() === 'true'

// Master stops never propagate on-request entries to daily occurrences.
const isStopExpectedOnRecurringDate = (stop, serviceDate) => {
  if (!stop || stop.isOnRequest) return false
  return hasRecurrenceOnDate(stop, serviceDate)
}

const toMasterOrderedStops = (masterOrderedStops, masterOrderedCseIds) => {
  if (Array.isArray(masterOrderedStops) && masterOrderedStops.length > 0) {
    return masterOrderedStops.map(s => ({
      workOrderId: s.workOrderId || null,
      configuredServiceEventId: s.configuredServiceEventId || s.cseId || null,
      isOnRequest: Boolean(s.isOnRequest),
      daysOfWeek: s.daysOfWeek || '',
      pricingPeriod: s.pricingPeriod || '',
      recurrence: s.recurrence || '',
      accountName: s.accountName || '',
      addressLine1: s.addressLine1 || '',
    }))
  }
  return (masterOrderedCseIds || []).filter(Boolean).map(cse => ({
    workOrderId: null,
    configuredServiceEventId: cse,
    isOnRequest: false,
    daysOfWeek: '',
    pricingPeriod: '',
    recurrence: '',
    accountName: '',
    addressLine1: '',
  }))
}

// Compact one-line description of a stop for diagnostic dumps. Falls back
// across name → address → CSE id → woId so something always renders.
const describeStop = stop => {
  if (!stop) return '?'
  const name = stop.accountName || stop.addressLine1 || ''
  const cse = stop.configuredServiceEventId ? ` cse=${stop.configuredServiceEventId.slice(0, 8)}` : ''
  const wo = stop.workOrderId ? ` wo=${stop.workOrderId.slice(0, 8)}` : ''
  const flag = stop.isOnRequest ? ' [on-request]' : ''
  const rec = stop.recurrence || stop.pricingPeriod
  const recur = rec ? ` ${rec}` : ''
  const dow = stop.daysOfWeek ? ` daysOfWeek=${stop.daysOfWeek}` : ''
  return `${name || '(unnamed)'}${cse}${wo}${flag}${recur}${dow}`
}

// `matchBy: 'cse'` required for permanent day-change dailies — WO IDs get
// reminted, CSE is the only stable identifier.
const stopKeyOf = (stop, matchBy) => {
  if (!stop) return null
  if (matchBy === 'cse') return stop.configuredServiceEventId || stop.cseId || null
  return stop.workOrderId || stop.id || null
}

const buildLandingRows = ({ expectedOrderedStops, actualOrderedStops, matchBy = 'workOrderId' }) => {
  const keyOf = s => stopKeyOf(s, matchBy)
  const expected = (expectedOrderedStops || []).filter(Boolean)
  const actual = (actualOrderedStops || []).filter(Boolean)
  const actualKeys = actual.map(keyOf)
  const total = actual.length
  const neighborLabel = (offset, pos) => {
    const idx = pos + offset
    if (idx < 0 || idx >= actual.length) return offset < 0 ? '— (start of route)' : '— (end of route)'
    return `#${idx + 1} ${describeStop(actual[idx])}`
  }
  return expected.map((stop, i) => {
    const key = keyOf(stop)
    const pos = key ? actualKeys.indexOf(key) : -1
    const landed = pos >= 0 ? pos + 1 : null
    const requested = i + 1
    let note = ''
    if (landed === null) {
      note = 'missing — not found on the day'
    } else if (landed !== requested) {
      const delta = landed - requested
      note = `shifted from requested #${requested} to #${landed} (Δ ${delta > 0 ? '+' : ''}${delta})`
    }
    const row = {
      stop: describeStop(stop),
      requestedSeq: requested,
      landedSeq: landed,
      totalOnDay: total,
      ok: landed === requested,
      note,
    }
    if (pos >= 0) {
      row.prev = neighborLabel(-1, pos)
      row.next = neighborLabel(+1, pos)
    }
    return row
  })
}

const buildDailyOrderView = ({ actualOrderedStops, expectedOrderedStops, matchBy = 'workOrderId' }) => {
  const keyOf = s => stopKeyOf(s, matchBy)
  const movedKeys = new Set((expectedOrderedStops || []).map(keyOf).filter(Boolean))
  return (actualOrderedStops || []).map((s, i) => {
    const key = keyOf(s)
    const marker = key && movedKeys.has(key) ? '◉' : ' '
    return `${marker} #${i + 1} ${describeStop(s)}`
  })
}

/* eslint-disable no-underscore-dangle */
export const isDebugEnabled = () => {
  if (typeof window !== 'undefined' && window.__debugRouteOps !== undefined) {
    return Boolean(window.__debugRouteOps)
  }
  return ENV_FLAG
}
/* eslint-enable no-underscore-dangle */

const time = () => {
  const d = new Date()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

const tag = () => TAG

const stopLabel = stop => {
  if (!stop) return '?'
  const name = stop.accountName || stop.serviceName || stop.addressName || ''
  const addr = stop.addressLine1 || stop.line1 || ''
  const id = (stop.workOrderId || stop.id || '').slice(0, 8)
  if (name) return addr ? `${name} ${addr}` : name
  return id || '?'
}

export const debugLog = (label, data) => {
  if (!isDebugEnabled()) return
  const prefix = `${time()}`
  if (data === undefined) console.log(tag(), TAG_STYLE, prefix, label)
  else console.log(tag(), TAG_STYLE, prefix, label, data)
}

/**
 * Wraps an RTK mutation/lazy-query trigger with request/response debug logging.
 * No-op when debug is off; signature is unchanged so it's a drop-in replacement.
 */
export const withDebugLogging = (label, trigger) => {
  if (typeof trigger !== 'function') return trigger
  return (...args) => {
    if (!isDebugEnabled()) return trigger(...args)
    const start = performance.now()
    console.log(tag(), TAG_STYLE, time(), `→ ${label}`, args[0])
    const result = trigger(...args)
    if (result && typeof result.unwrap === 'function') {
      result
        .unwrap()
        .then(res => console.log(tag(), TAG_STYLE, time(), `← ${label} (${Math.round(performance.now() - start)}ms)`, res))
        .catch(err => console.warn(tag(), TAG_STYLE, time(), `✗ ${label} (${Math.round(performance.now() - start)}ms)`, err))
    }
    return result
  }
}

export const debugGroup = (headline, fn) => {
  if (!isDebugEnabled()) {
    if (typeof fn === 'function') fn()
    return
  }
  console.groupCollapsed(tag(), TAG_STYLE, time(), headline)
  try {
    if (typeof fn === 'function') fn()
  } finally {
    console.groupEnd()
  }
}

export const debugAssert = (condition, message, context) => {
  if (!isDebugEnabled()) return condition
  if (condition) return true
  console.warn(tag(), TAG_STYLE, time(), `${FAIL} ${message}`, context)
  return false
}

// Symbol marker keeps wrapStep idempotent — builders + executePipeline may
// both try to wrap and we want one log line per step, not two.
const WRAPPED_MARKER = Symbol.for('routeOps.wrappedStep')

export const wrapStep = step => {
  if (!isDebugEnabled()) return step
  if (step && step[WRAPPED_MARKER]) return step
  return {
    ...step,
    [WRAPPED_MARKER]: true,
    fn: async () => {
      const start = performance.now()
      try {
        const result = await step.fn()
        debugLog(`${OK} ${step.phase || step.label} (${Math.round(performance.now() - start)}ms)`)
        return result
      } catch (err) {
        console.warn(tag(), TAG_STYLE, time(), `${FAIL} ${step.phase || step.label}`, err)
        throw err
      }
    },
  }
}

/**
 * Single-line operation headline, emitted once per user action.
 *
 * @param {Object} opts
 * @param {'ASSIGN'|'UNASSIGN'|'MOVE'|'RESEQUENCE'|'PUBLISH-MASTER'|'CHANGE-DAY'} opts.kind
 * @param {string} [opts.routeId]
 * @param {string} [opts.serviceDate]
 * @param {string} [opts.dayOfWeek]
 * @param {string} [opts.summary]
 * @param {Object[]} [opts.stops]
 */
export const debugOperation = ({ kind, routeId, serviceDate, dayOfWeek, summary, stops }) => {
  if (!isDebugEnabled()) return
  const parts = [kind.toUpperCase()]
  if (routeId) parts.push(routeId.slice(0, 8))
  if (dayOfWeek) parts.push(dayOfWeek)
  if (serviceDate) parts.push(serviceDate)
  if (summary) parts.push(summary)
  else if (Array.isArray(stops) && stops.length > 0) {
    const names = stops.slice(0, 5).map(stopLabel).join(', ')
    parts.push(stops.length > 5 ? `${names} …(+${stops.length - 5})` : names)
  }
  console.log(tag(), TAG_STYLE, time(), parts.join('  '))
}

/**
 * Refetches one daily schedule and compares its order to the expected list.
 * Logs a single-line result; any mismatch includes the reason.
 *
 * @returns {Promise<{ matched: boolean, reason?: string, actual?: Object[] } | null>}
 */
export const verifyDailySequence = async ({
  routeId,
  serviceDate,
  expectedOrderedStops,
  getDailyStops,
  beFinalSequence,
  indent = '  ├─',
}) => {
  if (!isDebugEnabled()) return null
  // Prefer the sequenceDaily response over a follow-up getDailyStops — RTK cache can lie.
  let actual
  if (Array.isArray(beFinalSequence) && beFinalSequence.length > 0) {
    actual = beFinalSequence
      .slice()
      .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
      .map(entry => ({
        workOrderId: entry.stopId || entry.workOrderId,
        sequence: entry.sequence,
      }))
  } else {
    if (!getDailyStops) return null
    try {
      const response = await getDailyStops({ routeId, serviceDate, includeTags: false }).unwrap()
      actual = (response?.stops || [])
        .slice()
        .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
        .map(s => ({
          workOrderId: s.workOrderId || s.id,
          configuredServiceEventId: s.configuredServiceEventId,
          sequence: s.sequence,
          accountName: s.accountName,
          addressLine1: s.addressLine1,
        }))
    } catch (err) {
      console.warn(tag(), TAG_STYLE, time(), `${indent} daily ${serviceDate}: ${FAIL} fetch failed`, err)
      return null
    }
  }
  try {
    const expectedIds = (expectedOrderedStops || []).map(s => s.workOrderId || s.id).filter(Boolean)
    const actualIds = actual.map(s => s.workOrderId)
    const expectedByWoId = new Map((expectedOrderedStops || []).map(s => [s.workOrderId || s.id, s]).filter(([k]) => k))

    const missing = expectedIds.filter(id => !actualIds.includes(id))
    const extra = actualIds.filter(id => !expectedIds.includes(id))
    const orderMatches = missing.length === 0 && expectedIds.every((id, i) => actualIds[i] === id)

    let reason = ''
    let icon = OK
    const expectedCount = expectedIds.length
    const actualCount = actualIds.length
    if (missing.length > 0 && extra.length === 0) {
      icon = INFO
      reason = `route count ${actualCount}/${expectedCount} (missing ${missing.length})`
    } else if (extra.length > 0 && missing.length === 0 && orderMatches) {
      icon = INFO
      reason = `route count ${actualCount} (expected ${expectedCount}; ${extra.length} extra beyond expected, likely on-request)`
    } else if (!orderMatches) {
      icon = FAIL
      const actualHeadList = actualIds.slice(0, 5).join(', ')
      const overflow = actualIds.length > 5 ? '…' : ''
      reason = `sequence mismatch — route count ${actualCount}/${expectedCount}, actual head [${actualHeadList}${overflow}]`
    }

    const defaultReason = `route count ${actualCount}/${expectedCount}, sequence matches`
    const headline = `${indent} daily ${serviceDate}: ${icon} ${reason || defaultReason}`
    const landings = buildLandingRows({
      expectedOrderedStops: expectedOrderedStops || [],
      actualOrderedStops: actual,
      matchBy: 'workOrderId',
    })
    const dailyOrder = buildDailyOrderView({
      actualOrderedStops: actual,
      expectedOrderedStops: expectedOrderedStops || [],
      matchBy: 'workOrderId',
    })
    if (icon === FAIL) {
      // Order mismatches on a single-date assign mean the resequence call
      // didn't land — show the ordered diff with names so the dev can spot
      // which stop slid into the wrong slot.
      console.warn(tag(), TAG_STYLE, time(), headline, {
        landings,
        dailyOrder,
        missing: missing.map(id => describeStop(expectedByWoId.get(id) || { workOrderId: id })),
        extra: extra.map(id => {
          const a = actual.find(x => x.workOrderId === id) || {}
          return describeStop({
            workOrderId: id,
            accountName: a.accountName,
            addressLine1: a.addressLine1,
            configuredServiceEventId: a.configuredServiceEventId,
          })
        }),
        expectedOrder: expectedIds.map(id => describeStop(expectedByWoId.get(id) || { workOrderId: id })),
        actualOrder: actual.map(s =>
          describeStop({
            workOrderId: s.workOrderId,
            accountName: s.accountName,
            addressLine1: s.addressLine1,
            configuredServiceEventId: s.configuredServiceEventId,
          })
        ),
        hint: 'sequenceDaily likely failed to land — compare expectedOrder vs actualOrder side-by-side.',
      })
    } else if (icon === INFO) {
      console.log(tag(), TAG_STYLE, time(), headline, {
        landings,
        dailyOrder,
        missingSample: missing.slice(0, 5).map(id => describeStop(expectedByWoId.get(id) || { workOrderId: id })),
        extraSample: extra.slice(0, 5).map(id => `wo=${(id || '').slice(0, 8)}`),
      })
    } else {
      console.log(tag(), TAG_STYLE, time(), headline, { landings, dailyOrder })
    }

    return { matched: orderMatches && missing.length === 0, reason, actual, missing, extra }
  } catch (err) {
    console.warn(tag(), TAG_STYLE, time(), `${indent} daily ${serviceDate}: ${FAIL} fetch failed`, err)
    return null
  }
}

/**
 * Verifies that the published master sequence propagated correctly to every
 * affected daily date. For each date, prints whether the daily order matches
 * the master CSE order, and explains any mismatch.
 *
 * Per-date expectations come from the BE preview when available, which is
 * the only correct source for arbitrary recurrence rules (Daily, Weekly,
 * Bi-weekly, Monthly, etc.). Without preview, falls back to a `daysOfWeek`
 * heuristic that's accurate for plain weekly stops only.
 *
 * Filters applied before comparison:
 *   - Master comparison drops on-request stops (they're never on master).
 *   - Per-date comparison drops both on-request and stops whose recurrence
 *     doesn't place them on that date (per preview, or per `daysOfWeek` in
 *     fallback mode).
 *
 * @param {Object} opts
 * @param {string} opts.routeId
 * @param {string} opts.dayOfWeek
 * @param {Object[]} [opts.masterOrderedStops] - { workOrderId, configuredServiceEventId, isOnRequest, daysOfWeek, pricingPeriod }
 * @param {string[]} [opts.masterOrderedCseIds] - back-compat fallback when only IDs are available
 * @param {string[]} [opts.affectedDates] - BE-format dates; derived from previewStops when omitted
 * @param {Object[]} [opts.previewStops] - raw preview.stops [{ serviceDate, workOrderId, ... }]
 * @param {Function} opts.getMasterStops - RTK lazy trigger
 * @param {Function} opts.getDailyStops  - RTK lazy trigger
 */
export const verifyMasterToDailyPropagation = async ({
  routeId,
  dayOfWeek,
  sourceServiceDate,
  masterOrderedStops,
  masterOrderedCseIds = [],
  batchCseIds,
  affectedDates,
  previewStops,
  getMasterStops,
  getDailyStops,
}) => {
  if (!isDebugEnabled()) return
  if (!getMasterStops && !getDailyStops) return

  const orderedStops = toMasterOrderedStops(masterOrderedStops, masterOrderedCseIds)
  const stopByCse = new Map(orderedStops.filter(s => s.configuredServiceEventId).map(s => [s.configuredServiceEventId, s]))
  const onRequestStops = orderedStops.filter(s => s.isOnRequest)

  // Master is day-of-week scoped. `orderedStops` is the current-day UI grid
  // which may contain CSEs that belong on OTHER days' masters (e.g. a Monday
  // recurring stop also appearing in today's Thursday daily). Scope the
  // master check to our batch's CSEs so we only assert about what the
  // operation actually published.
  const batchCseSet = batchCseIds && batchCseIds.length > 0 ? new Set(batchCseIds) : null
  const expectedMasterCseIds = batchCseSet
    ? orderedStops
        .filter(s => !s.isOnRequest && s.configuredServiceEventId && batchCseSet.has(s.configuredServiceEventId))
        .map(s => s.configuredServiceEventId)
    : orderedStops.filter(s => !s.isOnRequest && s.configuredServiceEventId).map(s => s.configuredServiceEventId)

  // Accumulated during the per-date loop; emitted as a per-stop summary at
  // the end so "where did each of my 3 stops land across master + dailies"
  // is one block, not scattered across per-date logs.
  let masterTotalCount = null
  let masterCseOrder = []
  const masterPositionByCse = new Map()
  const masterNeighborsByCse = new Map()
  const dailyLandingsByCse = new Map()

  const briefStop = s => {
    if (!s) return '?'
    const name = s.accountName || s.serviceName || s.addressName || ''
    const addr = s.addressLine1 || s.line1 || ''
    if (name && addr) return `${name} — ${addr}`
    return name || addr || '(unnamed)'
  }
  const neighborLabel = (arr, idx) => {
    if (idx < 0) return '— (start)'
    if (idx >= arr.length) return '— (end)'
    return `#${idx + 1} ${briefStop(arr[idx])}`
  }

  if (getMasterStops && expectedMasterCseIds.length > 0) {
    try {
      const res = await getMasterStops({ routeId, dayOfWeek }).unwrap()
      const masterStops = res?.stops || []
      const masterCses = masterStops.map(s => s.configuredServiceEventId)
      masterTotalCount = masterCses.length
      masterCseOrder = masterCses.filter(Boolean)
      masterCses.forEach((cse, idx) => {
        if (cse) {
          masterPositionByCse.set(cse, idx + 1)
          if (batchCseSet && batchCseSet.has(cse)) {
            masterNeighborsByCse.set(cse, {
              prev: neighborLabel(masterStops, idx - 1),
              next: neighborLabel(masterStops, idx + 1),
            })
          }
        }
      })
      const actualSet = new Set(masterCses)
      const missing = expectedMasterCseIds.filter(cse => !actualSet.has(cse))
      // Master commonly contains CSEs for other days of the week that our batch
      // doesn't know about. Treat extras as informational. The only real
      // failure signal for an assign/publish is "our CSEs didn't land".
      const expectedInMasterOrder = masterCses.filter(cse => expectedMasterCseIds.includes(cse))
      const orderedInBatch = expectedMasterCseIds.filter(cse => actualSet.has(cse))
      const orderMatches = missing.length === 0 && expectedInMasterOrder.every((cse, i) => orderedInBatch[i] === cse)

      const extraCount = masterCses.length - expectedInMasterOrder.length
      const extraNote = extraCount > 0 ? `, ${extraCount} unrelated CSE(s) ignored` : ''
      const onReqNote = onRequestStops.length > 0 ? `, ${onRequestStops.length} on-request ignored` : ''

      if (missing.length === 0 && orderMatches) {
        console.log(
          tag(),
          TAG_STYLE,
          time(),
          `  ├─ master ${dayOfWeek}: ${OK} ${expectedMasterCseIds.length}/${expectedMasterCseIds.length}${onReqNote}${extraNote}`
        )
      } else {
        const reasons = []
        if (missing.length) reasons.push(`${missing.length} missing`)
        if (!orderMatches && missing.length === 0) reasons.push('order differs')
        console.warn(tag(), TAG_STYLE, time(), `  ├─ master ${dayOfWeek}: ${FAIL} ${reasons.join(', ')}${onReqNote}${extraNote}`, {
          missing: missing.map(cse => describeStop(stopByCse.get(cse) || { configuredServiceEventId: cse })),
          expectedOrder: expectedMasterCseIds.map(cse => describeStop(stopByCse.get(cse) || { configuredServiceEventId: cse })),
          actualOrderAmongBatch: expectedInMasterOrder.map(cse => describeStop(stopByCse.get(cse) || { configuredServiceEventId: cse })),
          hint:
            missing.length > 0
              ? 'A "missing" CSE was expected on master but the BE never landed it. Check assignMaster/sequenceMaster step output.'
              : 'CSEs match by membership but the relative order among this batch differs — sequenceMaster may have failed silently.',
        })
      }
    } catch (err) {
      console.warn(tag(), TAG_STYLE, time(), `  ├─ master ${dayOfWeek}: ${FAIL} fetch failed`, err)
    }
  }

  // Per-date expectations, keyed by configuredServiceEventId. CSEs are stable
  // across occurrences whereas each future daily has its own workOrderId, so
  // the only reliable way to correlate preview output with our batch is via
  // the CSE. Preview already accounts for recurrence the BE supports
  // (Daily, Weekly, Bi-weekly, Monthly, ...) so we trust it over any FE
  // heuristic.
  const trackedCseSet = new Set(orderedStops.map(s => s.configuredServiceEventId).filter(Boolean))
  const previewCsesByDate = new Map()
  ;(previewStops || []).forEach(s => {
    if (!s?.serviceDate || !s.configuredServiceEventId) return
    if (trackedCseSet.size > 0 && !trackedCseSet.has(s.configuredServiceEventId)) return
    if (!previewCsesByDate.has(s.serviceDate)) previewCsesByDate.set(s.serviceDate, new Set())
    previewCsesByDate.get(s.serviceDate).add(s.configuredServiceEventId)
  })

  const datesToCheck =
    affectedDates && affectedDates.length > 0 ? affectedDates : [...previewCsesByDate.keys()].sort((a, b) => a.localeCompare(b))

  if (!getDailyStops || datesToCheck.length === 0) return

  for (let i = 0; i < datesToCheck.length; i++) {
    const date = datesToCheck[i]
    const isLast = i === datesToCheck.length - 1
    const indent = isLast ? '  └─' : '  ├─'
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await getDailyStops({ routeId, serviceDate: date, includeTags: false }).unwrap()
      const stops = res?.stops || []
      const dailyByCse = new Map(stops.filter(s => s.configuredServiceEventId).map(s => [s.configuredServiceEventId, s]))
      const dailyCses = stops.map(s => s.configuredServiceEventId).filter(Boolean)

      if (batchCseSet) {
        const total = stops.length
        const dailyCseSet = new Set(dailyCses)
        const isThisDateSource = sourceServiceDate && date === sourceServiceDate
        // Source-date daily was resequenced from the UI's intended order, so
        // master rank is the wrong expectation there — use the UI ordering.
        // Future dailies still fall back to master rank (BE cascade target).
        const sourceOrderIndexByCse = isThisDateSource
          ? new Map((orderedStops || []).filter(s => s.configuredServiceEventId).map((s, idx) => [s.configuredServiceEventId, idx]))
          : null
        batchCseSet.forEach(cse => {
          const pos = dailyCses.indexOf(cse)
          let expected = null
          let expectedFrom = null
          if (isThisDateSource && sourceOrderIndexByCse?.has(cse)) {
            expected = sourceOrderIndexByCse.get(cse) + 1
            expectedFrom = 'source-date ordered list'
          } else {
            // Expected position via master rank: 1 + count of master CSEs
            // that come before this one AND recur on this daily.
            const masterIdx = masterPositionByCse.get(cse) ?? 0
            if (masterIdx > 0) {
              expected = 1 + masterCseOrder.slice(0, masterIdx - 1).filter(c => dailyCseSet.has(c)).length
              expectedFrom = 'master rank'
            }
          }
          const neighbors = pos >= 0 ? { prev: neighborLabel(stops, pos - 1), next: neighborLabel(stops, pos + 1) } : null
          const existing = dailyLandingsByCse.get(cse) || []
          existing.push({
            date,
            pos: pos >= 0 ? pos + 1 : null,
            total,
            expected,
            expectedFrom,
            neighbors,
            missing: pos < 0,
          })
          dailyLandingsByCse.set(cse, existing)
        })
      }

      const isSourceDate = sourceServiceDate && date === sourceServiceDate
      let expectedForDate
      let expectedSource
      if (isSourceDate) {
        expectedForDate = orderedStops.filter(s => s.configuredServiceEventId && !s.isOnRequest).map(s => s.configuredServiceEventId)
        expectedSource = 'source-date ordered list'
      } else if (previewCsesByDate.has(date)) {
        // Preview tells us which of our batch's CSEs the BE expects to land
        // on this date. Those are the only stops we can assert about — the
        // rest of the daily is pre-existing route state.
        const trackedOnDate = previewCsesByDate.get(date)
        expectedForDate = orderedStops
          .filter(s => s.configuredServiceEventId && trackedOnDate.has(s.configuredServiceEventId))
          .map(s => s.configuredServiceEventId)
        expectedSource = 'preview (tracked-only)'
      } else {
        expectedForDate = orderedStops.filter(s => isStopExpectedOnRecurringDate(s, date)).map(s => s.configuredServiceEventId)
        expectedSource = 'daysOfWeek heuristic'
      }

      const expectedSet = new Set(expectedForDate)
      const missingCses = expectedForDate.filter(cse => !dailyCses.includes(cse))
      // Extras are "daily CSEs not in our expected set". When we're
      // intentionally scoping to tracked-only, everything else is a
      // pre-existing route stop and isn't our concern.
      const scopedToTracked = expectedSource === 'preview (tracked-only)'
      const extraCses = scopedToTracked ? [] : dailyCses.filter(cse => !expectedSet.has(cse))

      const commonInDailyOrder = dailyCses.filter(cse => expectedSet.has(cse))
      const commonInExpectedOrder = expectedForDate.filter(cse => dailyCses.includes(cse))
      const orderMatches = commonInDailyOrder.every((cse, idx) => commonInExpectedOrder[idx] === cse)

      const onReqOnDay = isSourceDate
        ? onRequestStops.filter(s => s.workOrderId && stops.some(d => (d.workOrderId || d.id) === s.workOrderId)).length
        : 0
      const onReqContext = (() => {
        if (onRequestStops.length === 0) return ''
        if (isSourceDate) return ` (incl. ${onReqOnDay}/${onRequestStops.length} on-request from source)`
        return ` (${onRequestStops.length} on-request scoped to ${sourceServiceDate || 'source date'})`
      })()

      const isHeuristic = expectedSource === 'daysOfWeek heuristic'
      let icon = OK
      const noteParts = []
      if (missingCses.length > 0) {
        icon = isHeuristic ? INFO : FAIL
        noteParts.push(`missing ${missingCses.length}${isHeuristic ? ' (heuristic)' : ''}`)
      }
      if (extraCses.length > 0) {
        if (icon === OK) icon = INFO
        noteParts.push(`${extraCses.length} extra`)
      }
      if (!orderMatches) {
        icon = FAIL
        noteParts.push('order does not match')
      }

      const landed = expectedForDate.filter(cse => dailyCses.includes(cse)).length
      const notesSuffix = noteParts.length ? ` — ${noteParts.join(', ')}` : ''
      const sourceTag = isSourceDate ? '  [source]' : ''
      const headline = scopedToTracked
        ? `${indent} daily ${date}: ${icon} ${landed}/${expectedForDate.length} tracked stop(s) landed${notesSuffix}${onReqContext}`
        : `${indent} daily ${date}: ${icon} ${stops.length}/${expectedForDate.length}${notesSuffix}${onReqContext}${sourceTag}`

      const expectedStopsForDate = expectedForDate.map(cse => stopByCse.get(cse) || { configuredServiceEventId: cse })
      const landings = buildLandingRows({
        expectedOrderedStops: expectedStopsForDate,
        actualOrderedStops: stops,
        matchBy: 'cse',
      })
      const dailyOrder = buildDailyOrderView({
        expectedOrderedStops: expectedStopsForDate,
        actualOrderedStops: stops,
        matchBy: 'cse',
      })

      if (icon === FAIL) {
        // Mismatch = warning sign — emit FULL diagnostic context. The dev
        // shouldn't need to re-run with breakpoints to figure out the why.
        const missingDetails = missingCses.map(cse => {
          const stop = stopByCse.get(cse) || { configuredServiceEventId: cse }
          let why = ''
          if (expectedSource === 'preview') why = 'preview placed this WO here but the daily fetch did not return it'
          else if (stop.isOnRequest) why = 'on-request stop expected by source-date logic — investigate'
          else if (stop.daysOfWeek) why = `daysOfWeek=${stop.daysOfWeek} suggests this date should include it`
          else why = 'no recurrence info — check stop manually'
          return { stop: describeStop(stop), why }
        })
        const extraDetails = extraCses.map(cse => {
          const dailyStop = dailyByCse.get(cse) || {}
          const known = stopByCse.get(cse)
          let why
          if (known && known.isOnRequest && !isSourceDate) why = 'on-request stop appearing on a non-source date — possible BE bug'
          else if (!known) why = 'not in this batch — pre-existing on the route or assigned by another flow'
          else why = 'in batch but not in expected-for-date set — check preview output'
          return {
            stop: describeStop({
              ...known,
              accountName: dailyStop.accountName || known?.accountName,
              configuredServiceEventId: cse,
            }),
            why,
          }
        })
        const orderDetails = orderMatches
          ? null
          : {
              expectedOrder: commonInExpectedOrder.map(cse => describeStop(stopByCse.get(cse) || { configuredServiceEventId: cse })),
              actualOrder: commonInDailyOrder.map(cse => describeStop(stopByCse.get(cse) || { configuredServiceEventId: cse })),
            }
        console.warn(tag(), TAG_STYLE, time(), headline, {
          expectedSource,
          sourceServiceDate,
          isSourceDate,
          onRequestInBatch: onRequestStops.length,
          landings,
          dailyOrder,
          missing: missingDetails,
          extra: extraDetails,
          ...(orderDetails || {}),
          hint: (() => {
            if (missingCses.length > 0 && expectedSource === 'preview')
              return 'Preview said these WOs land on this date but the daily endpoint disagrees — likely an assignFutureDailyOccurrences or master-cascade gap.'
            if (extraCses.length > 0 && extraCses.some(cse => stopByCse.get(cse)?.isOnRequest))
              return 'On-request stop landed on a recurring date — master cascade should never produce this.'
            return 'Order mismatch suggests a sequenceDaily/sequenceMaster step landed in the wrong order.'
          })(),
        })
      } else if (icon === INFO) {
        // Lower severity but still worth a structured peek so the dev can
        // confirm the extras are benign (typically pre-existing route stops).
        console.log(tag(), TAG_STYLE, time(), headline, {
          landings,
          dailyOrder,
          extraCount: extraCses.length,
          extraSample: extraCses.slice(0, 5).map(cse => `cse=${(cse || '').slice(0, 8)}`),
        })
      } else {
        console.log(tag(), TAG_STYLE, time(), headline, { landings, dailyOrder })
      }
    } catch (err) {
      console.warn(tag(), TAG_STYLE, time(), `${indent} daily ${date}: ${FAIL} fetch failed`, err)
    }
  }

  if (batchCseSet && batchCseSet.size > 0) {
    const summaryCseIds = [...batchCseSet]
    console.log(tag(), TAG_STYLE, time(), `  per-stop landings (batch of ${summaryCseIds.length}):`)
    summaryCseIds.forEach(cse => {
      const label = describeStop(stopByCse.get(cse) || { configuredServiceEventId: cse })
      const masterPos = masterPositionByCse.get(cse)
      const masterNeighbors = masterNeighborsByCse.get(cse)
      const masterLine =
        masterPos !== undefined
          ? `${OK} master ${dayOfWeek}: #${masterPos} of ${masterTotalCount ?? '?'}`
          : `${FAIL} master ${dayOfWeek}: missing (of ${masterTotalCount ?? '?'})`
      const dailyEntries = dailyLandingsByCse.get(cse) || []
      console.groupCollapsed(tag(), TAG_STYLE, time(), `    • ${label}`)
      console.log(`      ${masterLine}`)
      if (masterNeighbors) {
        console.log(`          prev: ${masterNeighbors.prev}`)
        console.log(`          next: ${masterNeighbors.next}`)
      }
      dailyEntries.forEach(({ date, pos, total, expected, expectedFrom, neighbors, missing: isMissing }) => {
        const via = expectedFrom ? ` via ${expectedFrom}` : ''
        let line
        if (isMissing) line = `${FAIL} daily ${date}: missing (of ${total})`
        else if (expected == null) line = `${OK} daily ${date}: #${pos} of ${total}`
        else if (pos === expected) line = `${OK} daily ${date}: #${pos} of ${total} (expected #${expected}${via})`
        else {
          const delta = pos - expected
          line = `${FAIL} daily ${date}: #${pos} of ${total} (expected #${expected}${via} — Δ ${delta > 0 ? '+' : ''}${delta})`
        }
        console.log(`      ${line}`)
        if (neighbors) {
          console.log(`          prev: ${neighbors.prev}`)
          console.log(`          next: ${neighbors.next}`)
        }
      })
      console.groupEnd()
    })
  }
}

export const verifyDailySequenceAfterRun = verifyDailySequence

/**
 * Cross-route sanity check for a daily-only assign/move. For each non-destination
 * route, fetches its daily stops and ensures none of the freshly-assigned work
 * order IDs appear there. Prints one line per checked route (`✓` clean / `✗`
 * leaked N / `✗` fetch failed). Safe to call concurrently with the destination
 * verification — each route is an independent try/catch.
 *
 * @returns {Promise<{ okRoutes: number, leakedRoutes: { routeId: string, leakedWorkOrderIds: string[] }[] } | null>}
 */
export const verifyAssignedStopsNotInOtherRoutes = async ({
  destinationRouteId,
  otherRouteIds = [],
  serviceDate,
  assignedWorkOrderIds = [],
  getDailyStops,
  indent = '  ├─',
}) => {
  if (!isDebugEnabled() || !getDailyStops) return null
  const targetSet = new Set((assignedWorkOrderIds || []).filter(Boolean))
  const otherIds = (otherRouteIds || []).filter(id => id && id !== destinationRouteId)
  if (targetSet.size === 0 || otherIds.length === 0) return { okRoutes: 0, leakedRoutes: [] }

  const results = await Promise.all(
    otherIds.map(async routeId => {
      const shortId = routeId.slice(0, 8)
      try {
        const res = await getDailyStops({ routeId, serviceDate, includeTags: false }).unwrap()
        const stops = res?.stops || []
        const leaked = stops.filter(s => targetSet.has(s.workOrderId || s.id))
        if (leaked.length === 0) {
          console.log(tag(), TAG_STYLE, time(), `${indent} cross-route ${serviceDate} ${shortId}: ${OK} clean`)
          return { routeId, ok: true }
        }
        console.warn(tag(), TAG_STYLE, time(), `${indent} cross-route ${serviceDate} ${shortId}: ${FAIL} ${leaked.length} leaked stop(s)`, {
          sample: leaked.slice(0, 5).map(describeStop),
          hint: 'These work orders were assigned to the destination route but still appear on another route for the same day — the source daily-unassign step may not have landed.',
        })
        return { routeId, ok: false, leakedWorkOrderIds: leaked.map(s => s.workOrderId || s.id) }
      } catch (err) {
        console.warn(tag(), TAG_STYLE, time(), `${indent} cross-route ${serviceDate} ${shortId}: ${FAIL} fetch failed`, err)
        return { routeId, ok: false, leakedWorkOrderIds: [] }
      }
    })
  )
  return {
    okRoutes: results.filter(r => r.ok).length,
    leakedRoutes: results.filter(r => !r.ok).map(({ routeId, leakedWorkOrderIds = [] }) => ({ routeId, leakedWorkOrderIds })),
  }
}

export const debugMasterToDailyPropagation = ({ routeId, sourceServiceDate, dates, masterOrderedCseIds }) => {
  debugLog(
    `master→daily queued — ${routeId?.slice(0, 8)} from ${sourceServiceDate}  dates=[${(dates || []).join(', ')}]  cse=${
      masterOrderedCseIds?.length || 0
    }`
  )
}

export const debugPipelinePlan = (pipelineName, params, steps) => {
  if (!isDebugEnabled()) return
  const phases = steps.map(s => s.phase || s.label).join(' → ')
  debugLog(`${pipelineName}  ${phases || '(no steps)'}`)
}

const safeInvoke = async fn => {
  try {
    return await fn()
  } catch (err) {
    debugLog('verify — swallowed error', err)
    return null
  }
}

// Unassign verifiers assert the target stops are ABSENT (the inverse of
// the assign verifiers, which assert ordered presence).

const verifyDailyUnassign = async ({
  routeId,
  serviceDate,
  unassignedWorkOrderIds = [],
  unassignedCseIds = [],
  getDailyStops,
  indent = '  ├─',
}) => {
  try {
    const res = await getDailyStops({ routeId, serviceDate, includeTags: false }).unwrap()
    const stops = res?.stops || []
    const woSet = new Set(unassignedWorkOrderIds.filter(Boolean))
    const cseSet = new Set(unassignedCseIds.filter(Boolean))
    const stillPresent = stops.filter(s => {
      const wo = s.workOrderId || s.id
      return woSet.has(wo) || cseSet.has(s.configuredServiceEventId)
    })
    const target = Math.max(woSet.size, cseSet.size)
    if (stillPresent.length === 0) {
      console.log(tag(), TAG_STYLE, time(), `${indent} daily ${serviceDate}: ${OK} unassigned ${target}/${target} (0 remain on day)`)
    } else {
      console.warn(tag(), TAG_STYLE, time(), `${indent} daily ${serviceDate}: ${FAIL} ${stillPresent.length} of ${target} still present`, {
        stillPresent: stillPresent.map(s => ({
          workOrderId: s.workOrderId || s.id,
          cse: s.configuredServiceEventId,
          sequence: s.sequence,
        })),
      })
    }
  } catch (err) {
    console.warn(tag(), TAG_STYLE, time(), `${indent} daily ${serviceDate}: ${FAIL} fetch failed`, err)
  }
}

const verifyMasterToDailyUnassign = async ({
  routeId,
  dayOfWeek,
  unassignedCseIds = [],
  unassignedWorkOrderIds = [],
  affectedDates = [],
  getMasterStops,
  getDailyStops,
}) => {
  if (!getMasterStops && !getDailyStops) return

  if (getMasterStops && unassignedCseIds.length > 0) {
    try {
      const res = await getMasterStops({ routeId, dayOfWeek }).unwrap()
      const masterCses = new Set((res?.stops || []).map(s => s.configuredServiceEventId).filter(Boolean))
      const stillOnMaster = unassignedCseIds.filter(cse => masterCses.has(cse))
      if (stillOnMaster.length === 0) {
        console.log(
          tag(),
          TAG_STYLE,
          time(),
          `  ├─ master ${dayOfWeek}: ${OK} unassigned ${unassignedCseIds.length}/${unassignedCseIds.length} (0 remain on master)`
        )
      } else {
        console.warn(
          tag(),
          TAG_STYLE,
          time(),
          `  ├─ master ${dayOfWeek}: ${FAIL} ${stillOnMaster.length} of ${unassignedCseIds.length} still on master`,
          { stillOnMaster }
        )
      }
    } catch (err) {
      console.warn(tag(), TAG_STYLE, time(), `  ├─ master ${dayOfWeek}: ${FAIL} fetch failed`, err)
    }
  }

  if (!getDailyStops || affectedDates.length === 0) return
  for (let i = 0; i < affectedDates.length; i++) {
    const date = affectedDates[i]
    const isLast = i === affectedDates.length - 1
    const indent = isLast ? '  └─' : '  ├─'
    // eslint-disable-next-line no-await-in-loop
    await verifyDailyUnassign({
      routeId,
      serviceDate: date,
      unassignedWorkOrderIds,
      unassignedCseIds,
      getDailyStops,
      indent,
    })
  }
}

/**
 * Returns a final pipeline step that verifies the daily sequence for a
 * single route+date. `kind: 'assign'` asserts stops landed in the UI's
 * intended order with matching count; `kind: 'unassign'` asserts the
 * target stops are gone. No-op when debug is off.
 */
export const makeVerifyDailyStep = ({
  kind = 'assign',
  routeId,
  serviceDate,
  expectedOrderedStops,
  unassignedWorkOrderIds,
  unassignedCseIds,
  // eslint-disable-next-line no-unused-vars
  otherRouteIds,
  // eslint-disable-next-line no-unused-vars
  assignedWorkOrderIds,
  getBeFinalSequence,
  deps,
}) => {
  if (!isDebugEnabled()) return null
  if (!deps?.getDailyStops) return null
  return {
    label: 'Debug verify daily',
    phase: 'DEBUG_VERIFY',
    fn: () =>
      safeInvoke(async () => {
        if (kind === 'unassign') {
          return verifyDailyUnassign({
            routeId,
            serviceDate,
            unassignedWorkOrderIds,
            unassignedCseIds,
            getDailyStops: deps.getDailyStops,
          })
        }
        return verifyDailySequence({
          routeId,
          serviceDate,
          expectedOrderedStops,
          getDailyStops: deps.getDailyStops,
          beFinalSequence: typeof getBeFinalSequence === 'function' ? getBeFinalSequence() : undefined,
        })
      }),
  }
}

/**
 * Returns a final pipeline step that verifies master→daily propagation.
 * Rediscovers affected dates via getRouteStopsPreview, then for each date:
 *   - kind=assign    → assert the daily CSE order matches the master order
 *   - kind=unassign  → assert the target CSEs/WO IDs are absent
 */
export const makeVerifyMasterToDailyStep = ({
  kind = 'assign',
  routeId,
  dayOfWeek,
  serviceDate,
  masterOrderedStops,
  masterOrderedCseIds,
  batchCseIds,
  unassignedCseIds,
  workOrderIds,
  deps,
}) => {
  if (!isDebugEnabled()) return null
  if (!deps?.getMasterStops && !deps?.getDailyStops) return null
  return {
    label: 'Debug verify master→daily',
    phase: 'DEBUG_VERIFY_MASTER',
    fn: () =>
      safeInvoke(async () => {
        let previewStops = []
        let affectedDates = []
        if (deps.getRouteStopsPreview && workOrderIds?.length) {
          const preview = await deps.getRouteStopsPreview({ stops: workOrderIds, serviceDate, updateMasterSequence: true }).unwrap()
          previewStops = preview?.stops || []
          affectedDates = [...new Set(previewStops.map(s => s.serviceDate))].sort((a, b) => a.localeCompare(b))
        }
        if (kind === 'unassign') {
          // Preview returns post-assign landings, so make sure the source date is swept too.
          const datesForSweep = serviceDate
            ? [...new Set([serviceDate, ...affectedDates])].sort((a, b) => a.localeCompare(b))
            : affectedDates
          return verifyMasterToDailyUnassign({
            routeId,
            dayOfWeek,
            unassignedCseIds,
            unassignedWorkOrderIds: workOrderIds,
            affectedDates: datesForSweep,
            getMasterStops: deps.getMasterStops,
            getDailyStops: deps.getDailyStops,
          })
        }
        return verifyMasterToDailyPropagation({
          routeId,
          dayOfWeek,
          sourceServiceDate: serviceDate,
          masterOrderedStops,
          masterOrderedCseIds,
          batchCseIds,
          affectedDates,
          previewStops,
          getMasterStops: deps.getMasterStops,
          getDailyStops: deps.getDailyStops,
        })
      }),
  }
}

const assertSourceDailyAbsent = async ({ routeId, serviceDate, workOrderIds = [], cseIds = [], indent, getDailyStops }) => {
  try {
    const res = await getDailyStops({ routeId, serviceDate, includeTags: false }).unwrap()
    const stops = res?.stops || []
    const woSet = new Set(workOrderIds.filter(Boolean))
    const cseSet = new Set(cseIds.filter(Boolean))
    const stillPresent = stops.filter(s => {
      const wo = s.workOrderId || s.id
      return woSet.has(wo) || (s.configuredServiceEventId && cseSet.has(s.configuredServiceEventId))
    })
    const target = Math.max(woSet.size, cseSet.size)
    if (stillPresent.length === 0) {
      console.log(tag(), TAG_STYLE, time(), `${indent} source daily ${serviceDate}: ${OK} moved ${target}/${target} absent from source`)
      return { matched: true }
    }
    console.warn(
      tag(),
      TAG_STYLE,
      time(),
      `${indent} source daily ${serviceDate}: ${FAIL} ${stillPresent.length}/${target} still present on source`,
      {
        stillPresent: stillPresent.map(s =>
          describeStop({
            workOrderId: s.workOrderId || s.id,
            configuredServiceEventId: s.configuredServiceEventId,
            accountName: s.accountName,
            addressLine1: s.addressLine1,
          })
        ),
        hint: 'Source daily unassign did not land — destination assign should have removed these from the source route for this date.',
      }
    )
    return { matched: false, stillPresent }
  } catch (err) {
    console.warn(tag(), TAG_STYLE, time(), `${indent} source daily ${serviceDate}: ${FAIL} fetch failed`, err)
    return null
  }
}

const assertSourceMasterAbsent = async ({ routeId, dayOfWeek, cseIds = [], indent, getMasterStops }) => {
  try {
    const res = await getMasterStops({ routeId, dayOfWeek }).unwrap()
    const masterCses = new Set((res?.stops || []).map(s => s.configuredServiceEventId).filter(Boolean))
    const stillOnMaster = cseIds.filter(cse => masterCses.has(cse))
    if (stillOnMaster.length === 0) {
      console.log(
        tag(),
        TAG_STYLE,
        time(),
        `${indent} source master ${dayOfWeek}: ${OK} moved ${cseIds.length}/${cseIds.length} absent from source master`
      )
      return { matched: true }
    }
    console.warn(
      tag(),
      TAG_STYLE,
      time(),
      `${indent} source master ${dayOfWeek}: ${FAIL} ${stillOnMaster.length}/${cseIds.length} still on source master`,
      {
        stillOnMaster,
        hint: 'Source master unassign did not land — moved CSEs should have been removed from source master for this day-of-week.',
      }
    )
    return { matched: false, stillOnMaster }
  } catch (err) {
    console.warn(tag(), TAG_STYLE, time(), `${indent} source master ${dayOfWeek}: ${FAIL} fetch failed`, err)
    return null
  }
}

/**
 * Pipeline step that mirrors the destination verifier on the SOURCE side of a
 * between-routes move: moved WO IDs absent from source's daily for the service
 * date, and moved CSE IDs absent from source's master for the day-of-week
 * (when `includeMaster` and `getMasterStops` are provided). Surfaces silent
 * source-side unassign failures.
 */
export const makeVerifyMoveSourceStep = ({
  sourceRouteId,
  serviceDate,
  dayOfWeek,
  workOrderIds = [],
  cseIds = [],
  includeMaster = false,
  deps,
}) => {
  if (!isDebugEnabled()) return null
  if (!sourceRouteId) return null
  if (!deps?.getDailyStops && !deps?.getMasterStops) return null
  if (workOrderIds.length === 0 && cseIds.length === 0) return null
  return {
    label: 'Debug verify move source absence',
    phase: 'DEBUG_VERIFY_MOVE_SOURCE',
    fn: () =>
      safeInvoke(async () => {
        const checkMaster = includeMaster && Boolean(deps.getMasterStops) && cseIds.length > 0
        const dailyIndent = checkMaster ? '  ├─' : '  └─'
        if (deps.getDailyStops && serviceDate && (workOrderIds.length > 0 || cseIds.length > 0)) {
          await assertSourceDailyAbsent({
            routeId: sourceRouteId,
            serviceDate,
            workOrderIds,
            cseIds,
            indent: dailyIndent,
            getDailyStops: deps.getDailyStops,
          })
        }
        if (checkMaster) {
          await assertSourceMasterAbsent({
            routeId: sourceRouteId,
            dayOfWeek,
            cseIds,
            indent: '  └─',
            getMasterStops: deps.getMasterStops,
          })
        }
      }),
  }
}

// ── BETWEEN-DATES MOVE VERIFIERS ──────────────────────────────────────
// Temporary: daily-only (changeDateDailyStops, no pipeline).
// Permanent: master day-of-week change (buildChangeDayPipeline, cascades).

const logOk = (line, context) =>
  context ? console.log(tag(), TAG_STYLE, time(), line, context) : console.log(tag(), TAG_STYLE, time(), line)
const logInfo = (line, context) => (context ? console.log(tag(), TAG_STYLE, time(), line, context) : logOk(line))
const logFail = (line, context) =>
  context ? console.warn(tag(), TAG_STYLE, time(), line, context) : console.warn(tag(), TAG_STYLE, time(), line)

const assertStopsAbsent = async ({ routeId, serviceDate, targetIdSet, cseIdSet = new Set(), sideLabel, indent, getDailyStops }) => {
  try {
    const res = await getDailyStops({ routeId, serviceDate, includeTags: false }).unwrap()
    const stops = res?.stops || []
    const stillPresent = stops.filter(
      s => targetIdSet.has(s.workOrderId || s.id) || (s.configuredServiceEventId && cseIdSet.has(s.configuredServiceEventId))
    )
    const target = Math.max(targetIdSet.size, cseIdSet.size)
    if (stillPresent.length === 0) {
      logOk(`${indent} ${sideLabel} daily ${serviceDate}: ${OK} gone ${target}/${target}`)
      return { matched: true }
    }
    logFail(`${indent} ${sideLabel} daily ${serviceDate}: ${FAIL} ${stillPresent.length}/${target} still present on source date`, {
      stillPresent: stillPresent.map(s =>
        describeStop({
          workOrderId: s.workOrderId || s.id,
          configuredServiceEventId: s.configuredServiceEventId,
          accountName: s.accountName,
          addressLine1: s.addressLine1,
        })
      ),
      hint: 'The source-date unassign step did not land — these stops should have been removed from this date.',
    })
    return { matched: false, stillPresent }
  } catch (err) {
    logFail(`${indent} ${sideLabel} daily ${serviceDate}: ${FAIL} fetch failed`, err)
    return null
  }
}

// `matchBy: 'cse'` required for permanent day-change — WO IDs are reminted
// on the destination, so only CSE survives the flip.
const assertStopsPresentOrdered = async ({
  routeId,
  serviceDate,
  expectedIds,
  expectedById,
  expectedOrderedStops,
  sideLabel,
  indent,
  getDailyStops,
  matchBy = 'workOrderId',
}) => {
  try {
    const res = await getDailyStops({ routeId, serviceDate, includeTags: false }).unwrap()
    const stops = res?.stops || []
    const keyOf = s => (matchBy === 'cse' ? s.configuredServiceEventId : s.workOrderId || s.id)
    const actualIds = stops.map(keyOf).filter(Boolean)
    const actualSet = new Set(actualIds)
    const missing = expectedIds.filter(id => !actualSet.has(id))

    let orderMatches = true
    if (expectedOrderedStops && expectedOrderedStops.length > 0) {
      const expectedFullIds = expectedOrderedStops
        .map(s => (matchBy === 'cse' ? s.configuredServiceEventId || s.cseId : s.workOrderId || s.id))
        .filter(Boolean)
      orderMatches = expectedFullIds.every((id, i) => actualIds[i] === id)
    } else {
      const commonInActualOrder = actualIds.filter(id => expectedIds.includes(id))
      orderMatches = expectedIds.every((id, i) => commonInActualOrder[i] === id)
    }

    const describeOne = id => {
      const fromMap = expectedById?.get(id)
      if (fromMap) return describeStop(fromMap)
      return describeStop(matchBy === 'cse' ? { configuredServiceEventId: id } : { workOrderId: id })
    }

    const expectedStopsForLanding =
      expectedOrderedStops && expectedOrderedStops.length > 0
        ? expectedOrderedStops
        : expectedIds.map(id => expectedById?.get(id) || (matchBy === 'cse' ? { configuredServiceEventId: id } : { workOrderId: id }))
    const landings = buildLandingRows({
      expectedOrderedStops: expectedStopsForLanding,
      actualOrderedStops: stops,
      matchBy,
    })
    const dailyOrder = buildDailyOrderView({
      expectedOrderedStops: expectedStopsForLanding,
      actualOrderedStops: stops,
      matchBy,
    })

    if (missing.length === 0 && orderMatches) {
      logOk(
        `${indent} ${sideLabel} daily ${serviceDate}: ${OK} route count ${stops.length} (${expectedIds.length} moved present, sequence matches)`,
        { landings, dailyOrder }
      )
      return { matched: true }
    }
    const noteParts = []
    if (missing.length) noteParts.push(`${missing.length} missing`)
    if (!orderMatches) noteParts.push('order differs')
    logFail(`${indent} ${sideLabel} daily ${serviceDate}: ${FAIL} ${noteParts.join(', ')}`, {
      matchBy,
      landings,
      dailyOrder,
      missing: missing.map(describeOne),
      expectedOrder: expectedIds.map(describeOne),
      actualOrderAmongMoved: actualIds.filter(id => expectedIds.includes(id)).map(describeOne),
      hint:
        missing.length > 0
          ? 'Moved stops did not land on the destination date — the assign-to-toDate step likely failed.'
          : 'Stops landed but sequence differs from what the UI intended — the resequence step may have run in the wrong order.',
    })
    return { matched: false, missing }
  } catch (err) {
    logFail(`${indent} ${sideLabel} daily ${serviceDate}: ${FAIL} fetch failed`, err)
    return null
  }
}

/**
 * Verifies a TEMPORARY daily-only move: moved WO ids are absent from
 * `fromServiceDate` and present (in UI order when provided) on
 * `toServiceDate`.
 */
export const verifyBetweenDatesDailyMove = async ({
  routeId,
  fromServiceDate,
  toServiceDate,
  movedWorkOrderIds = [],
  expectedDestinationOrderedStops,
  getDailyStops,
}) => {
  if (!isDebugEnabled() || !getDailyStops) return null
  if (!fromServiceDate || !toServiceDate || movedWorkOrderIds.length === 0) return null

  const targetIdSet = new Set(movedWorkOrderIds.filter(Boolean))
  const expectedByWoId = new Map(
    (expectedDestinationOrderedStops || []).filter(s => s?.workOrderId || s?.id).map(s => [s.workOrderId || s.id, s])
  )

  logOk(`between-dates (temporary) ${routeId?.slice(0, 8) || ''} ${fromServiceDate} → ${toServiceDate}  ${targetIdSet.size} stop(s)`)

  const sourceResult = await assertStopsAbsent({
    routeId,
    serviceDate: fromServiceDate,
    targetIdSet,
    sideLabel: 'from',
    indent: '  ├─',
    getDailyStops,
  })
  const destResult = await assertStopsPresentOrdered({
    routeId,
    serviceDate: toServiceDate,
    expectedIds: [...targetIdSet],
    expectedById: expectedByWoId,
    expectedOrderedStops: expectedDestinationOrderedStops,
    sideLabel: 'to',
    indent: '  └─',
    getDailyStops,
    matchBy: 'workOrderId',
  })
  return { source: sourceResult, destination: destResult }
}

const assertMasterDayAbsent = async ({ routeId, dayOfWeek, cseIdSet, getMasterStops }) => {
  try {
    const res = await getMasterStops({ routeId, dayOfWeek }).unwrap()
    const masterCses = new Set((res?.stops || []).map(s => s.configuredServiceEventId).filter(Boolean))
    const stillOnMaster = [...cseIdSet].filter(cse => masterCses.has(cse))
    if (stillOnMaster.length === 0) {
      logOk(`  ├─ master ${dayOfWeek} (from): ${OK} gone ${cseIdSet.size}/${cseIdSet.size}`)
      return { matched: true }
    }
    logFail(`  ├─ master ${dayOfWeek} (from): ${FAIL} ${stillOnMaster.length}/${cseIdSet.size} still on source master`, {
      stillOnMaster,
      hint: 'changeDayMaster should have moved these CSEs off the source day-of-week — investigate the master-unassign side of the cascade.',
    })
    return { matched: false, stillOnMaster }
  } catch (err) {
    logFail(`  ├─ master ${dayOfWeek} (from): ${FAIL} fetch failed`, err)
    return null
  }
}

const assertMasterDayPresentOrdered = async ({
  routeId,
  dayOfWeek,
  expectedCseIds,
  expectedByCse,
  expectedOrderedStops,
  stopByCse,
  getMasterStops,
}) => {
  try {
    const res = await getMasterStops({ routeId, dayOfWeek }).unwrap()
    const masterCses = (res?.stops || []).map(s => s.configuredServiceEventId).filter(Boolean)
    const actualSet = new Set(masterCses)
    const missing = expectedCseIds.filter(cse => !actualSet.has(cse))

    let orderMatches = true
    if (expectedOrderedStops && expectedOrderedStops.length > 0) {
      const expectedFullCseIds = expectedOrderedStops.map(s => s.configuredServiceEventId || s.cseId).filter(Boolean)
      const expectedInMaster = expectedFullCseIds.filter(cse => actualSet.has(cse))
      orderMatches = expectedInMaster.every((cse, i) => masterCses[i] === cse)
    } else {
      const commonInActualOrder = masterCses.filter(cse => expectedCseIds.includes(cse))
      orderMatches = expectedCseIds.every((cse, i) => commonInActualOrder[i] === cse)
    }

    if (missing.length === 0 && orderMatches) {
      logOk(`  ├─ master ${dayOfWeek} (to): ${OK} present ${expectedCseIds.length}/${expectedCseIds.length}, order matches`)
      return { matched: true }
    }
    const noteParts = []
    if (missing.length) noteParts.push(`${missing.length} missing`)
    if (!orderMatches) noteParts.push('order differs')
    logFail(`  ├─ master ${dayOfWeek} (to): ${FAIL} ${noteParts.join(', ')}`, {
      missing: missing.map(cse => describeStop((expectedByCse || stopByCse)?.get(cse) || { configuredServiceEventId: cse })),
      expectedOrder: expectedCseIds.map(cse => describeStop((expectedByCse || stopByCse)?.get(cse) || { configuredServiceEventId: cse })),
      actualOrderAmongMoved: masterCses
        .filter(cse => expectedCseIds.includes(cse))
        .map(cse => describeStop((expectedByCse || stopByCse)?.get(cse) || { configuredServiceEventId: cse })),
      hint:
        missing.length > 0
          ? 'changeDayMaster + assignMaster should have landed these CSEs on the target day-of-week.'
          : 'Order mismatch on destination master — sequenceMaster likely landed in the wrong order.',
    })
    return { matched: false, missing }
  } catch (err) {
    logFail(`  ├─ master ${dayOfWeek} (to): ${FAIL} fetch failed`, err)
    return null
  }
}

/**
 * Verifies a PERMANENT master day-of-week change: moved CSEs absent on
 * `fromDayOfWeek` master, present (in UI order when provided) on
 * `toDayOfWeek` master. When `getRouteStopsPreview` + `getDailyStops` +
 * `sourceServiceDate` are supplied, also verifies the cascaded dailies.
 */
export const verifyBetweenDatesMasterMove = async ({
  routeId,
  fromDayOfWeek,
  toDayOfWeek,
  movedCseIds = [],
  movedWorkOrderIds = [],
  destinationOrderedStops,
  movedRows,
  sourceServiceDate,
  getMasterStops,
  getDailyStops,
  getRouteStopsPreview,
}) => {
  if (!isDebugEnabled()) return null
  if (!getMasterStops || movedCseIds.length === 0) return null

  const cseIdSet = new Set(movedCseIds.filter(Boolean))
  const stopByCse = new Map(
    (movedRows || [])
      .filter(s => s?.configuredServiceEventId)
      .map(s => [
        s.configuredServiceEventId,
        {
          workOrderId: s.workOrderId || s.id || null,
          configuredServiceEventId: s.configuredServiceEventId,
          isOnRequest: isStopExpectedOnRecurringDate(s, sourceServiceDate) ? false : Boolean(s.isOnRequest),
          daysOfWeek: s.daysOfWeek || '',
          recurrence: s.recurrence || '',
          accountName: s.accountName || '',
          addressLine1: s.addressLine1 || s.line1 || '',
        },
      ])
  )

  logOk(`between-dates (permanent) ${routeId?.slice(0, 8) || ''} ${fromDayOfWeek} → ${toDayOfWeek}  ${cseIdSet.size} CSE(s)`)

  const srcMaster = await assertMasterDayAbsent({ routeId, dayOfWeek: fromDayOfWeek, cseIdSet, getMasterStops })
  const dstMaster = await assertMasterDayPresentOrdered({
    routeId,
    dayOfWeek: toDayOfWeek,
    expectedCseIds: [...cseIdSet],
    expectedOrderedStops: destinationOrderedStops,
    stopByCse,
    getMasterStops,
  })

  if (!getDailyStops || !getRouteStopsPreview || !sourceServiceDate || movedWorkOrderIds.length === 0) {
    return { sourceMaster: srcMaster, destinationMaster: dstMaster }
  }

  let previewStops = []
  try {
    const preview = await getRouteStopsPreview({
      stops: movedWorkOrderIds,
      serviceDate: sourceServiceDate,
      updateMasterSequence: true,
    }).unwrap()
    previewStops = preview?.stops || []
  } catch (err) {
    logInfo(`  ├─ preview fetch failed — skipping daily-cascade checks`, err)
    return { sourceMaster: srcMaster, destinationMaster: dstMaster }
  }

  const lc = s => String(s || '').toLowerCase()
  const dowOf = dateStr => {
    try {
      const d = new Date(`${dateStr}T00:00:00`)
      return lc(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][d.getDay()])
    } catch (e) {
      return ''
    }
  }

  const toDates = [...new Set(previewStops.filter(s => dowOf(s.serviceDate) === lc(toDayOfWeek)).map(s => s.serviceDate))].sort((a, b) =>
    a.localeCompare(b)
  )
  const fromDates = [...new Set(previewStops.filter(s => dowOf(s.serviceDate) === lc(fromDayOfWeek)).map(s => s.serviceDate))].sort(
    (a, b) => a.localeCompare(b)
  )

  // CSE-only keying: WO IDs are retired on the source and reminted on the
  // destination after changeDayMaster, so CSE is the only stable id here.
  const emptyIdSet = new Set()
  for (let i = 0; i < fromDates.length; i++) {
    const date = fromDates[i]
    const isLast = i === fromDates.length - 1 && toDates.length === 0
    const indent = isLast ? '  └─' : '  ├─'
    // eslint-disable-next-line no-await-in-loop
    await assertStopsAbsent({ routeId, serviceDate: date, targetIdSet: emptyIdSet, cseIdSet, sideLabel: 'from', indent, getDailyStops })
  }
  for (let i = 0; i < toDates.length; i++) {
    const date = toDates[i]
    const isLast = i === toDates.length - 1
    const indent = isLast ? '  └─' : '  ├─'
    // eslint-disable-next-line no-await-in-loop
    await assertStopsPresentOrdered({
      routeId,
      serviceDate: date,
      expectedIds: [...cseIdSet],
      expectedById: stopByCse,
      sideLabel: 'to',
      indent,
      getDailyStops,
      matchBy: 'cse',
    })
  }

  return { sourceMaster: srcMaster, destinationMaster: dstMaster, fromDates, toDates }
}

/**
 * Pipeline-step wrapper for `verifyBetweenDatesDailyMove`. Kept for
 * parity with the other `make*Step` helpers; current callers invoke
 * `verifyBetweenDatesDailyMove` directly since changeDateDailyStops runs
 * outside a pipeline.
 */
export const makeVerifyBetweenDatesDailyStep = ({
  routeId,
  fromServiceDate,
  toServiceDate,
  movedWorkOrderIds,
  expectedDestinationOrderedStops,
  deps,
}) => {
  if (!isDebugEnabled()) return null
  if (!deps?.getDailyStops) return null
  return {
    label: 'Debug verify between-dates daily',
    phase: 'DEBUG_VERIFY_BETWEEN_DATES',
    fn: () =>
      safeInvoke(() =>
        verifyBetweenDatesDailyMove({
          routeId,
          fromServiceDate,
          toServiceDate,
          movedWorkOrderIds,
          expectedDestinationOrderedStops,
          getDailyStops: deps.getDailyStops,
        })
      ),
  }
}

/**
 * Pipeline-step wrapper for `verifyBetweenDatesMasterMove`, appended to
 * `buildChangeDayPipeline` so the post-cascade master + daily checks run
 * after the BE flip.
 */
export const makeVerifyBetweenDatesMasterStep = ({
  routeId,
  fromDayOfWeek,
  toDayOfWeek,
  movedCseIds,
  movedWorkOrderIds,
  movedRows,
  destinationOrderedStops,
  sourceServiceDate,
  deps,
}) => {
  if (!isDebugEnabled()) return null
  if (!deps?.getMasterStops) return null
  return {
    label: 'Debug verify between-dates master',
    phase: 'DEBUG_VERIFY_BETWEEN_DATES_MASTER',
    fn: () =>
      safeInvoke(() =>
        verifyBetweenDatesMasterMove({
          routeId,
          fromDayOfWeek,
          toDayOfWeek,
          movedCseIds,
          movedWorkOrderIds,
          movedRows,
          destinationOrderedStops,
          sourceServiceDate,
          getMasterStops: deps.getMasterStops,
          getDailyStops: deps.getDailyStops,
          getRouteStopsPreview: deps.getRouteStopsPreview,
        })
      ),
  }
}

/**
 * Logs master state when `includeMaster=true` but no master writes were queued.
 */
export const makeVerifyMasterSkippedStep = ({ routeId, dayOfWeek, reason, deps }) => {
  if (!isDebugEnabled()) return null
  if (!deps?.getMasterStops) return null
  if (!routeId || !dayOfWeek) return null
  return {
    label: 'Debug verify master skipped',
    phase: 'DEBUG_VERIFY_MASTER_SKIPPED',
    fn: () =>
      safeInvoke(async () => {
        try {
          const res = await deps.getMasterStops({ routeId, dayOfWeek }).unwrap()
          const count = (res?.stops || []).length
          console.warn(
            tag(),
            TAG_STYLE,
            time(),
            `  └─ master ${dayOfWeek}: ${FAIL} publish skipped — ${count} stop(s) already on master, 0 added this run`,
            { reason, routeId, hint: 'The pipeline saw includeMaster=true but produced no master writes. Inspect the upstream stop data.' }
          )
        } catch (err) {
          console.warn(tag(), TAG_STYLE, time(), `  └─ master ${dayOfWeek}: ${FAIL} fetch failed`, err)
        }
      }),
  }
}

/**
 * Prints the one-line operation headline. No-op when debug off. Only the
 * pipeline builders call this — clients remain unaware.
 */
export const emitOperationHeadline = ({ kind, routeId, serviceDate, dayOfWeek, stopsCount, extra }) => {
  if (!isDebugEnabled()) return
  const parts = [kind]
  if (routeId) parts.push(routeId.slice(0, 8))
  if (dayOfWeek) parts.push(dayOfWeek)
  if (serviceDate) parts.push(serviceDate)
  if (stopsCount !== undefined) parts.push(`${stopsCount} stop${stopsCount === 1 ? '' : 's'}`)
  if (extra) parts.push(extra)
  console.log(tag(), TAG_STYLE, time(), parts.join('  '))
}
